import numeral from "numeral";

import "numeral/locales/pl";
import {NBSP, SUP2} from "./constants";
import {pluralize} from "./pluralize";
import {nowrap} from "./string";

/**
 * If you're looking for specific formatter, scroll down (line ~>= 90)
 */

// Select Polish locale from `numeral`
numeral.locale("pl");

// Interface for exported formatters (`numberFormat`, `areaFormat` etc.)
interface IOwnOptions {
    human?: boolean;
    precision?: number;
    unit?: string | [string, string, string];
    unitZero?: string;
}

// Interface for `numberParse`
interface IOptions extends IOwnOptions {
    unitRange?: string | [string, string];
    unitSeparator?: string;
    unitZero?: string;
}

// Wrapper for `numeral`
const baseNumberFormat = (value: number, precision = 2, human = false) => {
    // Build format for `numeral` method
    const precisionFormat = "0".repeat(precision);
    const humanFormat = human ? " a" : "";
    const numeralFormat = `0,0.[${precisionFormat}]${humanFormat}`;
    return numeral(value).format(numeralFormat).trim();
};

// Base formatter
const numberParse = (value: number | [number | null, number | null], options?: IOptions) => {
    // TODO: What the fuck am I doing?! Do it better...
    const precision = (options && options.precision) || 0;
    const human = (options && options.human) || false;
    const unit = (options && options.unit) || "";
    const unitRange = (options && options.unitRange) || "";
    const unitSeparator = (options && options.unitSeparator) || "";
    const unitZero = (options && options.unitZero) || "";

    // Value is 0
    if (value === 0) {
        return unitZero ? unitZero : value.toString();
    }

    // Value is a number other than 0
    if (typeof value === "number") {
        const parsedUnit = (Array.isArray(unit) && pluralize(unit)(value)) || unit;
        return nowrap(baseNumberFormat(value, precision, human) + unitSeparator + parsedUnit);
    }

    // Value is a range
    if (Array.isArray(value)) {
        // Format and filter falsy values
        const formattedValues = [
            value[0] && nowrap(`od${NBSP}${baseNumberFormat(value[0], precision, human)}`),
            value[1] && nowrap(`do${NBSP}${baseNumberFormat(value[1], precision, human)}`)
        ].filter((v) => !!v);

        // Range values are identical
        if (typeof value[0] === "number" && typeof value[1] === "number" && value[0] === value[1]) {
            const parsedUnit = (Array.isArray(unit) && pluralize(unit)(value[0])) || unit;
            return nowrap(baseNumberFormat(value[0], precision, human) + unitSeparator + parsedUnit);
        }

        // One of range value is falsy

        if (formattedValues.length === 1) {
            // We can assert number, because of formattedValues logic, however, we should rewrite this whole fucking shit along with url parsing logic
            const priceValue = (value[0] || value[1]) as number;
            const parsedUnit = (Array.isArray(unitRange) && pluralize([unitRange[0], unitRange[1], unitRange[1]])(priceValue)) || unitRange || unit;
            return nowrap(formattedValues[0] + unitSeparator! + parsedUnit);
        }

        // Range is correct
        const parsedUnit = (Array.isArray(unitRange) && unitRange[unitRange.length - 1]) || unitRange || unit;
        return formattedValues.join(" ") + unitSeparator + parsedUnit;
    }
};

// Custom, exported formatters
export const numberFormat = (value: number | [number, number], options?: IOwnOptions) => numberParse(value, options);

export const areaFormat = (value: number | [number | null, number | null], options?: IOwnOptions) =>
    numberParse(value, {unit: `m${SUP2}`, unitSeparator: NBSP, ...options});

export const distanceFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {precision: 2, unit: "m", unitSeparator: NBSP, ...options});

export const priceFormat = (value: number | [number | null, number | null], options?: IOwnOptions) =>
    numberParse(value, {unit: options?.unit || "zł", unitSeparator: NBSP, ...options});

export const priceM2Format = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {unitSeparator: NBSP, ...options, unit: `${options?.unit || "zł"}/m${SUP2}`});

export const roomsFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {
        unit: ["pokój", "pokoje", "pokoi"],
        unitRange: ["pokoju", "pokoi"],
        unitSeparator: NBSP,
        ...options
    });

export const floorFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {unit: "piętro", unitRange: "piętra", unitZero: "parter", unitSeparator: NBSP, ...options});

export const floorShortFormat = (value: number | [number, number], options?: IOwnOptions) => numberParse(value, {unitZero: "parter", ...options});

export const floorsFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {
        unit: ["kondygnacja", "kondygnacje", "kondygnacji"],
        unitRange: "kondygnacji",
        unitSeparator: NBSP,
        ...options
    });

export const bathroomsFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {
        unit: ["łazienka", "łazienki", "łazienek"],
        unitRange: ["łazienki", "łazienek"],
        unitSeparator: NBSP,
        ...options
    });

export const offersFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {
        unit: ["inwestycja", "inwestycje", "inwestycji"],
        unitRange: "inwestycji",
        unitSeparator: NBSP,
        ...options
    });

export const propertiesFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {
        unit: ["oferta", "oferty", "ofert"],
        unitRange: ["oferty", "ofert"],
        unitSeparator: NBSP,
        ...options
    });

export const flatsFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {
        unit: ["mieszkanie", "mieszkania", "mieszkań"],
        unitRange: "mieszkań",
        unitSeparator: NBSP,
        ...options
    });

export const housesFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {unit: ["dom", "domy", "domów"], unitRange: "domów", unitSeparator: NBSP, ...options});

export const commercialsFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {
        unit: ["lokal", "lokale", "lokali"],
        unitRange: ["lokale", "lokali"],
        unitSeparator: NBSP,
        ...options
    });

export const heightFormat = (value: number | [number, number], options?: IOwnOptions) => numberParse(value, {unit: "cm", unitSeparator: NBSP, ...options});

export const articlesFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {unit: ["artykuł", "artykuły", "artykułów"], unitSeparator: NBSP, ...options});

export const vendorsFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {unit: ["dewelopera", "deweloperów", "deweloperów"], unitSeparator: NBSP, ...options});

export const promotionsFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {unit: ["promocję", "promocje", "promocji"], unitSeparator: NBSP, ...options});

// prettier-ignore
const NATIONAL_AREA_CODES = [12, 13, 14, 15, 16, 17, 18, 22, 23, 24, 25, 29, 32, 33, 34, 41, 42, 43, 44, 46, 48, 52, 54, 55, 56, 58, 59, 61, 62, 63, 65, 67, 68, 71, 74, 75, 76, 77, 81, 82, 83, 84, 85, 86, 87, 89, 91, 94, 95];
export const phoneNumberFormat = (input: string): string => {
    if (!input) {
        return input;
    }
    const [countryCallingCode, phone] = input.split(" ");
    if (!phone) {
        // there are no spaces in phone number
        return input;
    }
    for (const code of NATIONAL_AREA_CODES) {
        if (phone.indexOf(code.toString()) == 0) {
            // format geographic number
            return nowrap(`+${countryCallingCode} ${code} ${format322(phone.slice(2))}`);
        }
    }
    // format mobile number
    return nowrap(`+${countryCallingCode} ${format333(phone)}`);
};
const format322 = (input: string): string => input.slice(0, 3) + " " + input.slice(3, 5) + " " + input.slice(5, 7);
const format333 = (input: string): string => input.slice(0, 3) + " " + input.slice(3, 6) + " " + input.slice(6, 9);

export const phoneNumberFormatNoPolishPrefix = (input: string) => {
    if (!input) {
        return input;
    }

    return phoneNumberFormat(input)
        .replace(/(^\+48)|(^48)/, "")
        .trim();
};

export const showPhoneNumberFormat = (phoneNumber: string) => {
    const whiteSpaceCount = (phoneNumber.match(/\u00a0/g) || []).length;
    return whiteSpaceCount == 3 ? phoneNumber.substring(0, 6) : phoneNumber.substring(0, 3);
};
import numeral from "numeral";

import "numeral/locales/pl";
import {NBSP, SUP2} from "./constants";
import {pluralize} from "./pluralize";
import {nowrap} from "./string";

/**
 * If you're looking for specific formatter, scroll down (line ~>= 90)
 */

// Select Polish locale from `numeral`
numeral.locale("pl");

// Interface for exported formatters (`numberFormat`, `areaFormat` etc.)
interface IOwnOptions {
    human?: boolean;
    precision?: number;
    unit?: string | [string, string, string];
    unitZero?: string;
}

// Interface for `numberParse`
interface IOptions extends IOwnOptions {
    unitRange?: string | [string, string];
    unitSeparator?: string;
    unitZero?: string;
}

// Wrapper for `numeral`
const baseNumberFormat = (value: number, precision = 2, human = false) => {
    // Build format for `numeral` method
    const precisionFormat = "0".repeat(precision);
    const humanFormat = human ? " a" : "";
    const numeralFormat = `0,0.[${precisionFormat}]${humanFormat}`;
    return numeral(value).format(numeralFormat).trim();
};

// Base formatter
const numberParse = (value: number | [number | null, number | null], options?: IOptions) => {
    // TODO: What the fuck am I doing?! Do it better...
    const precision = (options && options.precision) || 0;
    const human = (options && options.human) || false;
    const unit = (options && options.unit) || "";
    const unitRange = (options && options.unitRange) || "";
    const unitSeparator = (options && options.unitSeparator) || "";
    const unitZero = (options && options.unitZero) || "";

    // Value is 0
    if (value === 0) {
        return unitZero ? unitZero : value.toString();
    }

    // Value is a number other than 0
    if (typeof value === "number") {
        const parsedUnit = (Array.isArray(unit) && pluralize(unit)(value)) || unit;
        return nowrap(baseNumberFormat(value, precision, human) + unitSeparator + parsedUnit);
    }

    // Value is a range
    if (Array.isArray(value)) {
        // Format and filter falsy values
        const formattedValues = [
            value[0] && nowrap(`od${NBSP}${baseNumberFormat(value[0], precision, human)}`),
            value[1] && nowrap(`do${NBSP}${baseNumberFormat(value[1], precision, human)}`)
        ].filter((v) => !!v);

        // Range values are identical
        if (typeof value[0] === "number" && typeof value[1] === "number" && value[0] === value[1]) {
            const parsedUnit = (Array.isArray(unit) && pluralize(unit)(value[0])) || unit;
            return nowrap(baseNumberFormat(value[0], precision, human) + unitSeparator + parsedUnit);
        }

        // One of range value is falsy

        if (formattedValues.length === 1) {
            // We can assert number, because of formattedValues logic, however, we should rewrite this whole fucking shit along with url parsing logic
            const priceValue = (value[0] || value[1]) as number;
            const parsedUnit = (Array.isArray(unitRange) && pluralize([unitRange[0], unitRange[1], unitRange[1]])(priceValue)) || unitRange || unit;
            return nowrap(formattedValues[0] + unitSeparator! + parsedUnit);
        }

        // Range is correct
        const parsedUnit = (Array.isArray(unitRange) && unitRange[unitRange.length - 1]) || unitRange || unit;
        return formattedValues.join(" ") + unitSeparator + parsedUnit;
    }
};

// Custom, exported formatters
export const numberFormat = (value: number | [number, number], options?: IOwnOptions) => numberParse(value, options);

export const areaFormat = (value: number | [number | null, number | null], options?: IOwnOptions) =>
    numberParse(value, {unit: `m${SUP2}`, unitSeparator: NBSP, ...options});

export const distanceFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {precision: 2, unit: "m", unitSeparator: NBSP, ...options});

export const priceFormat = (value: number | [number | null, number | null], options?: IOwnOptions) =>
    numberParse(value, {unit: options?.unit || "zł", unitSeparator: NBSP, ...options});

export const priceM2Format = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {unitSeparator: NBSP, ...options, unit: `${options?.unit || "zł"}/m${SUP2}`});

export const roomsFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {
        unit: ["pokój", "pokoje", "pokoi"],
        unitRange: ["pokoju", "pokoi"],
        unitSeparator: NBSP,
        ...options
    });

export const floorFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {unit: "piętro", unitRange: "piętra", unitZero: "parter", unitSeparator: NBSP, ...options});

export const floorShortFormat = (value: number | [number, number], options?: IOwnOptions) => numberParse(value, {unitZero: "parter", ...options});

export const floorsFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {
        unit: ["kondygnacja", "kondygnacje", "kondygnacji"],
        unitRange: "kondygnacji",
        unitSeparator: NBSP,
        ...options
    });

export const bathroomsFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {
        unit: ["łazienka", "łazienki", "łazienek"],
        unitRange: ["łazienki", "łazienek"],
        unitSeparator: NBSP,
        ...options
    });

export const offersFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {
        unit: ["inwestycja", "inwestycje", "inwestycji"],
        unitRange: "inwestycji",
        unitSeparator: NBSP,
        ...options
    });

export const propertiesFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {
        unit: ["oferta", "oferty", "ofert"],
        unitRange: ["oferty", "ofert"],
        unitSeparator: NBSP,
        ...options
    });

export const flatsFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {
        unit: ["mieszkanie", "mieszkania", "mieszkań"],
        unitRange: "mieszkań",
        unitSeparator: NBSP,
        ...options
    });

export const housesFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {unit: ["dom", "domy", "domów"], unitRange: "domów", unitSeparator: NBSP, ...options});

export const commercialsFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {
        unit: ["lokal", "lokale", "lokali"],
        unitRange: ["lokale", "lokali"],
        unitSeparator: NBSP,
        ...options
    });

export const heightFormat = (value: number | [number, number], options?: IOwnOptions) => numberParse(value, {unit: "cm", unitSeparator: NBSP, ...options});

export const articlesFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {unit: ["artykuł", "artykuły", "artykułów"], unitSeparator: NBSP, ...options});

export const vendorsFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {unit: ["dewelopera", "deweloperów", "deweloperów"], unitSeparator: NBSP, ...options});

export const promotionsFormat = (value: number | [number, number], options?: IOwnOptions) =>
    numberParse(value, {unit: ["promocję", "promocje", "promocji"], unitSeparator: NBSP, ...options});

// prettier-ignore
const NATIONAL_AREA_CODES = [12, 13, 14, 15, 16, 17, 18, 22, 23, 24, 25, 29, 32, 33, 34, 41, 42, 43, 44, 46, 48, 52, 54, 55, 56, 58, 59, 61, 62, 63, 65, 67, 68, 71, 74, 75, 76, 77, 81, 82, 83, 84, 85, 86, 87, 89, 91, 94, 95];
export const phoneNumberFormat = (input: string): string => {
    if (!input) {
        return input;
    }
    const [countryCallingCode, phone] = input.split(" ");
    if (!phone) {
        // there are no spaces in phone number
        return input;
    }
    for (const code of NATIONAL_AREA_CODES) {
        if (phone.indexOf(code.toString()) == 0) {
            // format geographic number
            return nowrap(`+${countryCallingCode} ${code} ${format322(phone.slice(2))}`);
        }
    }
    // format mobile number
    return nowrap(`+${countryCallingCode} ${format333(phone)}`);
};
const format322 = (input: string): string => input.slice(0, 3) + " " + input.slice(3, 5) + " " + input.slice(5, 7);
const format333 = (input: string): string => input.slice(0, 3) + " " + input.slice(3, 6) + " " + input.slice(6, 9);

export const phoneNumberFormatNoPolishPrefix = (input: string) => {
    if (!input) {
        return input;
    }

    return phoneNumberFormat(input)
        .replace(/(^\+48)|(^48)/, "")
        .trim();
};

export const showPhoneNumberFormat = (phoneNumber: string) => {
    const whiteSpaceCount = (phoneNumber.match(/\u00a0/g) || []).length;
    return whiteSpaceCount == 3 ? phoneNumber.substring(0, 6) : phoneNumber.substring(0, 3);
};
